import React from 'react';
import { Globe, Users, Award, Headphones } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const stats = [
  { value: "100+", label: "Happy Clients", icon: Users },
  { value: "10+", label: "Services Offered", icon: Globe },
  { value: "24/7", label: "Support Available", icon: Headphones },
  { value: "100%", label: "Client Satisfaction", icon: Award }
];

const AboutSection = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <section id="about" className={`relative py-24 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="about-section">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? 'rgba(0, 180, 216, 0.25)' : 'rgba(0, 180, 216, 0.15)'} 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`text-sm uppercase tracking-[0.3em] font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>About NEURA AI</span>
          <h2 className={`text-4xl md:text-5xl font-extralight mt-4 mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Built for
            <span className={`font-normal ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}> Ambitious Businesses</span>
          </h2>
          <p className={`text-lg max-w-3xl mx-auto leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            We're a Belfast-based web agency helping UK businesses establish a powerful online presence.
            From stunning websites to SEO, e-commerce, and AI chatbots—we handle everything so you can focus on what you do best.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
                <div className="relative p-6 rounded-2xl border border-gray-200 bg-white hover:border-cyan-300 transition-all duration-500 shadow-sm hover:shadow-lg text-center">
                  <IconComponent className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">
                    {stat.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-white mb-6">
                Why UK Businesses Choose Us
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <span><strong className="text-white">All-Inclusive Service</strong> — Hosting, SSL, maintenance, and support included as standard</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <span><strong className="text-white">UK-Based Team</strong> — Real humans in Belfast, not offshore call centers</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <span><strong className="text-white">No Hidden Fees</strong> — Transparent service with no surprise charges</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                  <span><strong className="text-white">Quality Guarantee</strong> — We don't stop until you're completely satisfied</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-2xl blur-xl opacity-30" />
              <div className="relative bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <p className="text-gray-400 italic mb-4">
                  "NEURA AI built us a beautiful website, set up our online booking system, and even added an AI chatbot.
                  Our inquiries have tripled and we spend zero time on website maintenance. Best decision we made this year!"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                    <span className="text-white font-bold">JM</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">James Morrison</p>
                    <p className="text-gray-500 text-sm">Owner, Morrison Consulting</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Founder Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Founded by <span className="text-gray-900 font-medium">Peter Gwaze</span> in Belfast, Northern Ireland
          </p>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
